// ============================================================
// AUTH — runtime-mode router
// ============================================================
// Re-exports either the Cognito-backed auth or the mock auth based on
// whether real Cognito credentials are configured. The rest of the
// codebase imports from 'lib/auth.js' and gets the right one.
//
// Resilience: if auth-cognito.js fails to load (e.g. amazon-cognito-identity-js
// not installed in this build), we fall back to the mock so the SPA still
// boots in a degraded but observable state — captureError surfaces the
// reason in Sentry instead of a hard white-screen.

import { getRuntimeMode } from './runtime-mode.js';
import { captureError } from './sentry.js';
import { closeTelemetryStream } from './telemetry-stream.js';
import { getImpersonation } from './impersonation.js';
import { isDemoMode, getDemoSession } from './demo-customer.js';

const useCognito = getRuntimeMode().realAuth;

async function loadImpl() {
  if (!useCognito) return import('./auth-mock.js');
  try {
    return await import('./auth-cognito.js');
  } catch (err) {
    captureError(err, { scope: 'auth-router', message: 'Failed to load Cognito impl; falling back to mock' });
    // eslint-disable-next-line no-console
    console.error('[auth] Cognito impl failed to load — falling back to mock', err);
    return import('./auth-mock.js');
  }
}

const impl = await loadImpl();

export const {
  signIn,
  forgotPassword,
  confirmForgotPassword,
  beginMfaEnrollment,
  verifyMfaCode,
  getPendingChallenge,
  cancelPendingChallenge,
} = impl;

// isAuthenticated / getSession are demo-aware: a public guest who clicked
// "View a live demo" has no real Cognito session, but an active demo session
// should render the portal as authenticated (mock data only; every real
// backend call is blocked by installDemoFetchGuard). A real signed-in session
// always takes precedence over the synthetic demo identity.
export function isAuthenticated() {
  if (impl.isAuthenticated()) return true;
  try { return isDemoMode(); } catch { return false; }
}
export function getSession() {
  const real = impl.getSession();
  if (real) return real;
  try { if (isDemoMode()) return getDemoSession(); } catch { /* fall through */ }
  return null;
}

// getAccountNumber is impersonation-aware: when a netcom-admin (or an
// org-parent using the AccountSwitcher) is acting as another account, the
// EFFECTIVE account is the impersonated one — that's what every page
// should load data for. Falls back to the session's own account when not
// impersonating. The base impl's getAccountNumber (session-only) is kept
// available as getOwnAccountNumber for the rare caller that needs the
// signed-in user's real account regardless of impersonation.
export function getOwnAccountNumber() { return impl.getAccountNumber(); }
export function getAccountNumber() {
  try {
    const imp = getImpersonation();
    if (imp?.accountNumber) return imp.accountNumber;
  } catch { /* fall through */ }
  return impl.getAccountNumber();
}
// Optional — only the Cognito-backed impl exports this. The mock impl
// doesn't need it (mock users have permanent passwords baked in).
export const completeNewPassword = impl.completeNewPassword || (async () => ({
  success: false,
  error: 'completeNewPassword is not available in mock auth.',
}));

// Wrap signOut so we always tear down the WebSocket telemetry stream too.
// Calling signOut while a live WS holds a (now-revoked) access token is a
// real-world leak vector; this guarantees cleanup.
export async function signOut() {
  try { closeTelemetryStream(); } catch (e) { captureError(e, { scope: 'auth:signOut:closeStream' }); }
  return impl.signOut();
}
